/**
 * **A small trend on a task card** — one line naming what is drawn with the newest reading beside
 * it, and the plot under it: a movement's e1RM beside a clip or a strength goal, a client's body
 * weight under a weight goal (owner, 26 September 2026). `ChartCardShell` holds the four states and
 * the plot's reserve; this is the head and the chart those two callers draw alike.
 */

import type { ReactNode } from 'react';

import type { ChartPoint } from '~/shared/lib/chartSeries';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';

import { ChartCardShell } from './ChartCardShell';
import { ReadoutChart } from './ReadoutChart';
import styles from './TrendCard.module.css';

/** What the plot draws once the read has answered with something. */
export interface TrendSeries {
  /** What the series is, for the chart's summary: „e1RM", „Masa ciała". */
  readonly name: string;
  readonly unit: string;
  readonly better: 'higher' | 'lower';
  /** Present when the figures print as a clock rather than as numbers. */
  readonly figure: ((value: number) => string) | null;
  readonly points: readonly ChartPoint[];
  /** The newest reading with its unit, printed in the head. */
  readonly latest: string;
}

export interface TrendCardProps {
  readonly title: ReactNode;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly failedTitle: ReactNode;
  readonly emptyTitle: ReactNode;
  /** `null` while waiting, after a failure, or when there is nothing to draw. */
  readonly series: TrendSeries | null;
  readonly reducedMotion: boolean;
}

export function TrendCard({ title, gate, failed, failedTitle, emptyTitle, series, reducedMotion }: TrendCardProps) {
  return (
    <ChartCardShell
      className={styles.trend}
      height="var(--h-task-trend-plot)"
      head={
        <p className={styles.head}>
          <span className={styles.title}>{title}</span>
          <span className={`${String(styles.latest)} ${gate.reveal}`}>
            {gate.waiting || series === null ? ' ' : series.latest}
          </span>
        </p>
      }
      gate={gate}
      failed={failed}
      failedTitle={failedTitle}
      empty={series === null}
      emptyTitle={emptyTitle}
    >
      {series === null ? null : (
        <ReadoutChart
          className={gate.reveal}
          colour="var(--chart-series-neutral)"
          better={series.better}
          {...(series.figure === null ? {} : { figure: series.figure })}
          height="var(--h-task-trend-plot)"
          name={series.name}
          points={series.points}
          reducedMotion={reducedMotion}
          unit={series.unit}
          zeroBaseline={false}
          compact
        />
      )}
    </ChartCardShell>
  );
}
